import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar,
  ShoppingCart,
  DollarSign,
  Clock,
  ChefHat,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  TrendingDown,
  Users,
  Leaf,
  ExternalLink,
  Play
} from 'lucide-react';
import {
  DEMO_MEAL_PLAN,
  DEMO_GROCERY_LIST,
  DEMO_SAVINGS,
  DEMO_PATHS,
  calculateDemoTotals
} from '@/data/demoData';

/**
 * Demo Mode Landing
 * 
 * Full product experience without login.
 * Pre-loaded meals, prices, savings - the complete value prop.
 */

const DemoLanding = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plan');
  const totals = calculateDemoTotals();

  const handleStartPlanning = () => {
    navigate('/meal-planner?demo=true');
  };

  const handleViewList = () => {
    navigate('/grocery-lists?demo=true');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background" data-testid="demo-landing">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-b">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-primary/20 text-primary">
                <Play className="h-3 w-3 mr-1" />
                Demo Mode
              </Badge>
              <span className="text-sm text-muted-foreground">No login required</span>
            </div>
            <Button onClick={() => navigate('/auth')} variant="outline" size="sm">
              Sign Up Free
            </Button>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold font-['Playfair_Display'] mb-3">
            This Week with Carted
          </h1>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl">
            See how meal planning eliminates decision fatigue. This is a real week - 
            6 dinners, one grocery trip, zero stress.
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-2 text-primary mb-1">
                <ChefHat className="h-4 w-4" />
                <span className="text-sm font-medium">Meals Planned</span>
              </div>
              <p className="text-2xl font-bold">{DEMO_SAVINGS.this_week.meals_planned}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm font-medium">Est. Cost</span>
              </div>
              <p className="text-2xl font-bold">${DEMO_SAVINGS.this_week.estimated_cost}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <TrendingDown className="h-4 w-4" />
                <span className="text-sm font-medium">You Save</span>
              </div>
              <p className="text-2xl font-bold text-green-600">${DEMO_SAVINGS.this_week.savings}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Users className="h-4 w-4" />
                <span className="text-sm font-medium">Servings</span>
              </div>
              <p className="text-2xl font-bold">{DEMO_SAVINGS.this_week.servings}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto">
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Meal Plan
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Grocery List
            </TabsTrigger>
            <TabsTrigger value="savings" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Savings
            </TabsTrigger>
          </TabsList>

          {/* Meal Plan Tab */}
          <TabsContent value="plan" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="text-2xl">🥗</span>
                  {DEMO_MEAL_PLAN.path_name}
                </h2>
                <p className="text-sm text-muted-foreground">5 weeknight dinners + Sunday meal prep</p>
              </div>
              <Button onClick={handleStartPlanning} data-testid="customize-plan-btn">
                Customize This Plan
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="grid gap-4">
              {DEMO_MEAL_PLAN.days.map((day) => (
                <Card key={day.name} className={`${day.meals.length === 0 ? 'opacity-50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-20 text-center">
                        <p className="font-semibold text-primary">{day.name}</p>
                        {day.meals.length === 0 && (
                          <p className="text-xs text-muted-foreground">Rest day</p>
                        )}
                      </div>
                      
                      {day.meals.map((meal) => (
                        <div key={meal.id} className="flex-1 flex items-center gap-4">
                          <img 
                            src={meal.recipe_image} 
                            alt={meal.recipe_title}
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium">{meal.recipe_title}</h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {meal.prep_time + meal.cook_time} min
                              </span>
                              <span>${meal.price_estimate.toFixed(2)}</span>
                            </div>
                            <div className="flex gap-1 mt-2">
                              {meal.health_labels?.map((label) => (
                                <Badge key={label} variant="outline" className="text-xs">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Grocery List Tab */}
          <TabsContent value="list" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Your Grocery List</h2>
                <p className="text-sm text-muted-foreground">
                  {totals.items_to_buy} items to buy • {totals.pantry_items_skipped} pantry staples skipped
                </p>
              </div>
              <Button onClick={handleViewList} variant="outline" data-testid="export-list-btn">
                <ExternalLink className="h-4 w-4 mr-2" />
                Export to Instacart
              </Button>
            </div>

            {/* Group by aisle */}
            {Object.entries(
              DEMO_GROCERY_LIST.items.reduce((acc, item) => {
                if (!acc[item.aisle]) acc[item.aisle] = [];
                acc[item.aisle].push(item);
                return acc;
              }, {})
            ).map(([aisle, items]) => (
              <Card key={aisle}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    {aisle}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {items.map((item, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between py-2 ${item.checked ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          {item.checked ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                          )}
                          <span className={item.checked ? 'line-through text-muted-foreground' : ''}>
                            {item.name}
                          </span>
                          {item.amount > 0 && (
                            <span className="text-sm text-muted-foreground">
                              {item.amount} {item.unit}
                            </span>
                          )}
                          {item.note && (
                            <Badge variant="secondary" className="text-xs">
                              {item.note}
                            </Badge>
                          )}
                        </div>
                        {item.price > 0 && (
                          <span className="text-sm font-medium">${item.price.toFixed(2)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Total */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Estimated Total</span>
                  <span className="text-2xl font-bold">${totals.subtotal}</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Savings Tab */}
          <TabsContent value="savings" className="space-y-6">
            <h2 className="text-xl font-semibold">Your Savings This Week</h2>

            {/* Savings Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-green-600" />
                    vs. Unplanned Shopping
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Typical unplanned week</span>
                    <span className="font-medium">${DEMO_SAVINGS.this_week.typical_unplanned_cost}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">With Carted planning</span>
                    <span className="font-medium">${DEMO_SAVINGS.this_week.estimated_cost}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-600">You save</span>
                      <span className="text-2xl font-bold text-green-600">
                        ${DEMO_SAVINGS.this_week.savings} ({DEMO_SAVINGS.this_week.savings_percent}%)
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ChefHat className="h-5 w-5 text-amber-600" />
                    vs. Eating Out
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Restaurant equivalent</span>
                    <span className="font-medium">${DEMO_SAVINGS.this_week.restaurant_equivalent}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Home cooking cost</span>
                    <span className="font-medium">${DEMO_SAVINGS.this_week.estimated_cost}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-600">You save</span>
                      <span className="text-2xl font-bold text-green-600">
                        ${DEMO_SAVINGS.this_week.vs_restaurant_savings}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Smart Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Smart Savings Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {DEMO_SAVINGS.insights.map((insight, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {insight.type === 'overlap' && <Leaf className="h-5 w-5 text-green-600" />}
                      {insight.type === 'bulk' && <ShoppingCart className="h-5 w-5 text-blue-600" />}
                      {insight.type === 'seasonal' && <Sparkles className="h-5 w-5 text-amber-600" />}
                      <span className="text-sm">{insight.message}</span>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      +${insight.savings.toFixed(2)}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Cost per serving */}
            <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="py-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Cost per serving</p>
                  <p className="text-4xl font-bold text-primary">
                    ${DEMO_SAVINGS.this_week.cost_per_serving}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {DEMO_SAVINGS.this_week.servings} home-cooked meals for your family
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <Card className="mt-8 bg-gradient-to-r from-primary to-primary/80 text-white border-0">
          <CardContent className="py-8">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-3">Ready to plan your week?</h3>
              <p className="text-primary-foreground/80 mb-6">
                Create your free account and get your first meal plan in under 5 minutes.
              </p>
              <div className="flex items-center justify-center gap-4">
                <Button 
                  size="lg" 
                  variant="secondary"
                  onClick={() => navigate('/auth')}
                  data-testid="demo-signup-btn"
                >
                  Sign Up Free
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={handleStartPlanning}
                >
                  Keep Exploring Demo
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemoLanding;
